import { useEffect, useMemo, useState } from "react";
import { fetchRecentLineItems, type RecentLineItem, type RecentLineItemsResult, type Settings } from "../api";

const presets = [1, 2, 3, 7, 14, 30];

const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const formatStatus = (financial: string | null, fulfillment: string | null) =>
    `${financial ?? "—"} · ${fulfillment ?? "unfulfilled"}`;

const customerLabel = (item: { customer_name: string | null; customer_email: string | null }) =>
    item.customer_name || item.customer_email || "—";

interface VariantSubgroup {
    key: string;
    variant_title: string | null;
    sku: string | null;
    qty: number;
    items: RecentLineItem[];
}

interface ProductGroup {
    key: string;
    title: string;
    total_qty: number;
    order_count: number;
    variants: VariantSubgroup[];
}

function groupByProduct(items: RecentLineItem[]): ProductGroup[] {
    const products = new Map<string, ProductGroup>();
    const variantsByProduct = new Map<string, Map<string, VariantSubgroup>>();

    for (const item of items) {
        const productKey = item.product_id ?? `title:${item.title}`;
        const variantKey = `${productKey}::${item.variant_id ?? item.variant_title ?? "default"}`;

        let pg = products.get(productKey);
        if (!pg) {
            pg = { key: productKey, title: item.title, total_qty: 0, order_count: 0, variants: [] };
            products.set(productKey, pg);
            variantsByProduct.set(productKey, new Map());
        }
        pg.total_qty += item.quantity;

        const variantMap = variantsByProduct.get(productKey)!;
        let vg = variantMap.get(variantKey);
        if (!vg) {
            vg = {
                key: variantKey,
                variant_title: item.variant_title,
                sku: item.sku,
                qty: 0,
                items: [],
            };
            variantMap.set(variantKey, vg);
            pg.variants.push(vg);
        }
        vg.qty += item.quantity;
        vg.items.push(item);
    }

    // Compute per-product order counts (distinct order_ids across variants).
    for (const pg of products.values()) {
        const orders = new Set<string>();
        for (const v of pg.variants) for (const it of v.items) orders.add(it.order_id);
        pg.order_count = orders.size;
        pg.variants.sort((a, b) =>
            (a.variant_title ?? "").localeCompare(b.variant_title ?? "")
        );
        for (const v of pg.variants) {
            v.items.sort((a, b) => b.processed_at.localeCompare(a.processed_at));
        }
    }

    return [...products.values()].sort((a, b) => a.title.localeCompare(b.title));
}

export function OrdersView({ settings }: { settings: Settings }) {
    const [days, setDays] = useState(7);
    const [data, setData] = useState<RecentLineItemsResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setErr(null);
        setExpanded(new Set());
        fetchRecentLineItems(settings, days)
            .then((d) => !cancelled && setData(d))
            .catch((e) => !cancelled && setErr((e as Error).message))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [settings, days]);

    const groups = useMemo(() => (data ? groupByProduct(data.items) : []), [data]);

    const toggle = (key: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    return (
        <div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-xs uppercase tracking-wide text-okiru-muted mr-2">Last</span>
                {presets.map((n) => (
                    <button
                        key={n}
                        onClick={() => setDays(n)}
                        className={`text-xs px-3 py-1.5 rounded-md border transition ${
                            days === n
                                ? "border-okiru-accent text-okiru-accent"
                                : "border-okiru-muted/30 hover:border-okiru-accent"
                        }`}
                    >
                        {n}d
                    </button>
                ))}
            </div>

            {err && (
                <div className="bg-red-950 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4 text-sm">
                    {err}
                </div>
            )}

            {loading ? (
                <div className="text-okiru-muted">Loading…</div>
            ) : groups.length === 0 ? (
                <div className="text-okiru-muted text-sm">No orders in window.</div>
            ) : (
                <div className="bg-okiru-panel border border-okiru-muted/15 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-okiru-bg/50 text-okiru-muted text-xs uppercase tracking-wide">
                                <tr>
                                    <th className="text-left px-4 py-3 font-medium w-8" />
                                    <th className="text-left px-4 py-3 font-medium">Product</th>
                                    <th className="text-right px-4 py-3 font-medium">Total Qty</th>
                                    <th className="text-left px-4 py-3 font-medium">Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((g) => (
                                    <ProductRows
                                        key={g.key}
                                        group={g}
                                        isOpen={expanded.has(g.key)}
                                        onToggle={() => toggle(g.key)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="text-xs text-okiru-muted px-4 py-2 border-t border-okiru-muted/15">
                        {groups.length} products ·{" "}
                        {groups.reduce((acc, g) => acc + g.variants.length, 0)} variants ·{" "}
                        {data?.items.length ?? 0} line items ·{" "}
                        {new Set(data?.items.map((i) => i.order_id)).size} orders
                    </div>
                </div>
            )}
        </div>
    );
}

function ProductRows({
    group,
    isOpen,
    onToggle,
}: {
    group: ProductGroup;
    isOpen: boolean;
    onToggle: () => void;
}) {
    const variantCount = group.variants.length;
    const detail =
        variantCount === 1
            ? `${group.order_count} order${group.order_count === 1 ? "" : "s"}`
            : `${variantCount} variants · ${group.order_count} order${group.order_count === 1 ? "" : "s"}`;

    return (
        <>
            <tr
                className="border-t border-okiru-muted/15 cursor-pointer hover:bg-okiru-bg/40"
                onClick={onToggle}
            >
                <td className="px-4 py-2.5 text-okiru-muted">
                    <span
                        className="inline-block transition-transform"
                        style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}
                    >
                        ▸
                    </span>
                </td>
                <td className="px-4 py-2.5 font-medium">{group.title}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-medium">{group.total_qty}</td>
                <td className="px-4 py-2.5 text-okiru-muted text-xs">{detail}</td>
            </tr>
            {isOpen && (
                <tr className="bg-okiru-bg/30">
                    <td colSpan={4} className="px-4 py-3">
                        <div className="space-y-3">
                            {group.variants.map((v) => (
                                <VariantBlock key={v.key} variant={v} />
                            ))}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

function VariantBlock({ variant }: { variant: VariantSubgroup }) {
    return (
        <div className="border border-okiru-muted/15 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between bg-okiru-panel/60 px-3 py-2 text-xs">
                <span className="text-okiru-text font-medium">{variant.variant_title ?? "—"}</span>
                <span className="text-okiru-muted tabular-nums">
                    {variant.qty} bag{variant.qty === 1 ? "" : "s"}
                </span>
            </div>
            <table className="w-full text-xs">
                <thead className="text-okiru-muted">
                    <tr>
                        <th className="text-left px-3 py-1.5 font-medium">Customer</th>
                        <th className="text-left px-3 py-1.5 font-medium">Order</th>
                        <th className="text-left px-3 py-1.5 font-medium">Date</th>
                        <th className="text-right px-3 py-1.5 font-medium">Qty</th>
                        <th className="text-left px-3 py-1.5 font-medium">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {variant.items.map((item, idx) => (
                        <tr
                            key={`${item.order_id}:${item.variant_id ?? item.sku ?? idx}`}
                            className="border-t border-okiru-muted/10"
                        >
                            <td className="px-3 py-1.5">{customerLabel(item)}</td>
                            <td className="px-3 py-1.5 text-okiru-muted">{item.order_name}</td>
                            <td className="px-3 py-1.5 text-okiru-muted whitespace-nowrap">
                                {formatDate(item.processed_at)}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{item.quantity}</td>
                            <td className="px-3 py-1.5 text-okiru-muted">
                                {formatStatus(item.financial_status, item.fulfillment_status)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
